/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {isObject, isUndefined} from 'lodash';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import {getDeviceIdentifier} from '@ion/applications/src/pages/devices/ViewDeviceEditorUtils';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadSubtasks} from '@ion/core/src/services/Connect';
import {
  AssetAPIResult,
  Assets,
  AsyncTaskStatus,
  BulkActionEntity,
  BulkActionType,
  BulkGroupAssignmentInfo,
  CTCEntities,
  DeviceAPIResult,
  DeviceFiles,
  Devices,
  InventoryDevices,
  JobStatus,
  RoleAPIResult,
  Roles,
  Subtask
} from '@ion/core/src/types/entities';
import {RequestTypeLabels, RequestTypes} from '@ion/core/src/types/general';
import {SubtaskResult} from '@ion/core/src/types/services';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {
  idMap,
  isObjectEmpty
} from '@ion/core/src/utils/ObjectUtil';
import {capitalizeFirstLetter} from '@ion/core/src/utils/StringUtil';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {
  getFeaturesStatus,
  isDeviceRequest,
  isEnableServicesRequest,
  isRequestType
} from './JobStatusUtils';

const tooltipStyle = makeStyles(theme => ({
  arrow : {
    color : dlsColor('black')
  },
  tooltip : {
    ...dlsAlt('components', 'iconTooltip'),
    fontSize : '14px !important'
  },
  align : {
    overflow     : 'hidden',
    textOverflow : 'ellipsis'
  }
}));

// the number of rows the grid should cache & display to the user
export const CACHE_BLOCK_SIZE = 100;

/**
   * The datasource necessary for the grid's infinite scroll capability.
   *
   * This will handle local caching of any overflow from the CACHE_BLOCK_SIZE due to issues with extra 'Loading...'
   * rows showing when the resulting rows are fewer than the CACHE_BLOCK_SIZE, so since we are parsing the subtasks
   * for entities, the subtasks can contain less than the CACHE_BLOCK_SIZE of rows, then displaying the extra
   * loading rows.
   *
   * So this will fetch new subtasks, parse the entities out, and fetch more if there aren't enough.
   * If there are no more to fetch, the datasource will simply return the rows it has and provide the rowCount to
   * the grid, telling it to not fetch any further subtasks (& entities).
   *
   * @param api the grid api
   * @returns infinite scroll grid datasource
   */
export const getDataSource = ({
  api,
  mainTaskId,
  isBulkOperation,
  requestType,
  setRowData,
  isBulkExport
}) => {
  let nextCursor = '';
  let totalGridRowCount = 0;
  let leftoverEntities = [];
  let finalEntities = [];
  let rowCount = -1;
  let masterList = [];

  const setRemainingEntities = (entities): void => {
    finalEntities = entities;
    totalGridRowCount += finalEntities.length;
    rowCount = totalGridRowCount;
    leftoverEntities = [];
  };

  const subtasksDatasource = {
    getRows : async({fail, success, request}) => {
      if (request) {
        const {groupKeys, rowGroupCols} = request || {};
        const isGroupingActive = groupKeys?.length && rowGroupCols?.length;
        if (isGroupingActive) {
          let groupRows;
          const isRowGroupExpansion = groupKeys?.length;
          if (isRowGroupExpansion) {
            const matchingGroupRow = masterList.find(entity => entity?.name === groupKeys[0]);
            groupRows = matchingGroupRow?.subResults || [];
          }
          success({rowData: groupRows, rowCount: groupRows?.length});
          return;
        }
      }
      finalEntities = [];
      rowCount = -1;
      try {
        if (hasAnotherPage(nextCursor)) {
          let consolidatedEntities = leftoverEntities;
          while (hasFewerRowsThanCache(consolidatedEntities)) {
            if (hasAnotherPage(nextCursor)) {
              const {
                subtasks,
                cursor,
                errors,
                error
              } = await fetchSubtasks(mainTaskId, nextCursor, isBulkOperation) || {};
              nextCursor = cursor;
              const bulkOperation = {
                isBulkOperation,
                errors,
                error
              };
              const newEntities = parseEntities(subtasks, requestType, bulkOperation);
              consolidatedEntities = consolidatedEntities.concat(newEntities);
            } else {
              break;
            }
          }
          if (hasFewerRowsThanCache(consolidatedEntities)) {
            setRemainingEntities(consolidatedEntities);
          } else {
            finalEntities = consolidatedEntities.slice(0, CACHE_BLOCK_SIZE);
            leftoverEntities = consolidatedEntities.slice(CACHE_BLOCK_SIZE);
            totalGridRowCount= totalGridRowCount + finalEntities.length;
          }
        } else {
          setRemainingEntities(leftoverEntities);
        }
        if (!finalEntities.length) {
          api.showNoRowsOverlay();
        }
        setRowData(previous => previous.concat(finalEntities));
        masterList = [...masterList, ...finalEntities];
        success({rowData: finalEntities, rowCount});
      } catch (err) {
        fail();
        api.showNoRowsOverlay();
      }
    }
  };
  return subtasksDatasource;
};

/**
 * Determines if there is another page of subtasks to fetch based on the given cursor.
 *
 * Empty string is the default, meaning we haven't attempted to fetch a page yet.
 * When there are no more pages to fetch the server will not return a cursor, resulting in the
 * explicit value of undefined being returned.
 *
 * @param cursor the cursor to evaluate
 * @returns true if there is another page to fetch, false otherwise
 */
const hasAnotherPage = (cursor: string): boolean => (
  !isUndefined(cursor)
);

/**
 * Determines if we should fetch more entities.
 *
 * @param entities the entities to evaluate the count of
 * @returns true if the number of rows is less than the cache block size (including 0), false otherwise.
 */
const hasFewerRowsThanCache = (entities: CTCEntities): boolean => {
  if (entities?.length) {
    return entities.length <= CACHE_BLOCK_SIZE;
  }
  return true;
};

/**
 * Fetches subtasks & handles any errors.
 *
 * @param id the main task id to fetch subtasks of
 * @param cursor the encrypted id of the last returned subtask for seamless pagination
 * @param isBulkOperation if the main task is for a bulk operation
 * @param isBulkExport if the main task is for a bulk export operation
 * @returns SubtaskResult if successful, otherwise logs any error and re-throws it
 */
const fetchSubtasks = async(
  id: string,
  cursor: string,
  isBulkOperation: boolean
): Promise<SubtaskResult> => {
  try {
    const result = await loadSubtasks(id, cursor, isBulkOperation);
    return result;
  } catch (err) {
    error(err);
    throw err;
  }
};

/**
 * Parses any returned entities from the given subtasks.
 *
 * @param subtasks the subtasks to parse entities from
 * @param requestType
 * @param isBulkOperation
 * @returns
 */
const parseEntities = (
  subtasks: Subtask[],
  requestType: RequestTypeLabels,
  bulkOperation: BulkGroupAssignmentInfo
): Subtask[] => {
  let newEntities = [];
  if (!bulkOperation?.isBulkOperation) {
    newEntities = getBulkEntitySubtasks(subtasks, requestType);
  } else {
    newEntities = getBulkOperationEntities(subtasks, bulkOperation);
  }
  return newEntities;
};

/**
 * Parses any bulk entities from the given subtasks (for non-bulk-operations).
 *
 * @param subtasks subtasks returned for the main task
 * @param requestType the type of entity requested
 * @returns entities embedded in subtasks
 */
const getBulkEntitySubtasks = (subtasks: Subtask[], requestType) => {
  const result = [];
  subtasks?.forEach(item => {
    const {
      lastModifiedAt,
      status,
      other,
      name,
      info
    } = item || {};
    const entityInfoResponse              = safeJSONParse(info?.response?.body);
    if (entityInfoResponse) {
      const {response: {errors, results}} = entityInfoResponse || {};
      const subTasksList                  = item[RequestTypes[requestType]];
      const parsedErrors: EntityErrors    = [];
      const idMaps                        = createDeviceMappings(subTasksList, other?.requestType) || {};
      if (subTasksList?.length) {
        if (errors?.create?.length || errors?.update?.length || errors?.read?.length) {
          const {update, read, create} = errors || {};
          read?.length && parsedErrors.push(...parseImportErrors(read));
          update?.length && parsedErrors.push(...parseImportErrors(update));
          create?.length && parsedErrors.push(...parseImportErrors(create));
        }
        subTasksList?.forEach(record => {
          const {
            name,
            lastModifiedOn
          }: ApiResult = resolveResultDetails(idMaps, record, other?.requestType) || {};
          const selectedIdentifier   = getDeviceIdentifier(record);
          const deviceIdentifierType = getDeviceIdentifierType(other?.requestType, record);
          const {features} = record || {};
          result.push({
            name           : name || getJobStatusName(record, requestType),
            features       : getFeaturesList(features),
            lastModifiedOn : lastModifiedOn ? formatDateTime(lastModifiedOn) : formatDateTime(lastModifiedAt),
            status         : AsyncTaskStatus.SUCCESS,
            identifier     : selectedIdentifier,
            identifierType : deviceIdentifierType
          });
        });
        parsedErrors?.forEach(error => {
          const {name, description, code} = error || {};
          const {
            name: deviceName,
            lastModifiedOn
          }: ApiResult = resolveResultDetails(idMaps, name, other?.requestType);
          result.push({
            lastModifiedOn : lastModifiedOn ? formatDateTime(lastModifiedOn) : '',
            name           : deviceName || name,
            status         : AsyncTaskStatus.FAILED,
            reason         : description,
            errorCode      : code
          });
        });
      } else {
        if (errors?.create?.length || errors?.update?.length || errors?.read?.length) {
          const {update, read, create} = errors || {};
          read?.length && parsedErrors.push(...parseImportErrors(read));
          update?.length && parsedErrors.push(...parseImportErrors(update));
          create?.length && parsedErrors.push(...parseImportErrors(create));
        } if (results?.create?.length || results?.update?.length || results?.read?.length) {
          const {update, read, create} = results || {};
          read?.length && parsedErrors.push(...parseImportErrors(read));
          update?.length && parsedErrors.push(...parseImportErrors(update));
          create?.length && parsedErrors.push(...parseImportErrors(create));
        }
        parsedErrors?.forEach(error => {
          const {name, description, code} = error || {};
          const {
            name: deviceName,
            lastModifiedOn
          }: ApiResult = resolveResultDetails(idMaps, name, other?.requestType);
          result.push({
            lastModifiedOn : lastModifiedOn ? formatDateTime(lastModifiedOn) : '',
            name           : deviceName || name,
            status         : status,
            reason         : description,
            errorCode      : code
          });
        });
      }
    } else {
      result.push({
        lastModifiedOn : formatDateTime(lastModifiedAt),
        name           : name,
        status
      });
    }
  });
  return result;
};

const getJobStatusName = ({firstName, lastName, name}, requestType) => {
  if (isRequestType(requestType, RequestTypeLabels.USER) && firstName) {
    return `${firstName} ${lastName}`;
  }
  return name || '';
};

const getFeaturesList = (features) => {
  if (features) {
    const featureNames = Object.entries(features).map(([key, value]) => `${key} - ${value}`);
    return featureNames.join(', ');
  }
  return '';
};

// Possible device identifier types returned from API, these are how we map results to their respective device detail.
enum DeviceIdentifierType {
  ESN = 'esn',
  ID = 'id',
  DEVICE_ID = 'deviceId',
  SERIAL_NUMBER = 'serialNumber',
  MEID = 'meid',
  ICCID = 'iccid',
  IMEI = 'imei',
  IMEI2 = 'imei2',
  IMSI = 'imsi',
  MDN = 'mdn',
  IP_ADDRESS = 'ipAddress',
  EXTERNAL_ID = 'externalId',
  NAME = 'name',
  DEVICE_TYPE_NAME = 'deviceTypeName'
}

// Possible device identifier type value to show in Identifier Type column.
enum identifierTypeColumnKeys {
  esn = 'ESN',
  serialNumber = 'Serial Number',
  iccid = 'ICCID',
  meid = 'MEID',
  imei = 'IMEI',
  imei2 = 'IMEI2',
  imsi = 'IMSI',
  mdn = 'MDN',
  externalId = 'External ID',
  ipAddress = 'Ip Address',
  id = 'ID',
  deviceId = 'ID',
  name = 'Name',
  deviceTypeName = 'Device Type Name'
}

// Possible asset identifier types returned from API, these are how we map results to their respective asset detail.
enum AssetIdentifierType {
  VIN = 'vin',
  ID = 'id',
  ASSET_ID = 'assetId',
  LICENSE_NUMBER = 'licenseNumber',
  NAME = 'name'
}

// Possible role identifier types returned from API, these are how we map results to their respective role detail.
enum RoleIdentifierType {
  ROLE_ID = 'roleId',
  ID = 'id'
}

// Possible file meta identifier types returned from API, these are how we map results to their respective file detail.
enum FileMetaIdentifierType {
  NAME = 'name',
  FILE_META_ID = 'filemetaId'
}

enum BulkActivityStatusEntityName {
  DEVICE = 'device',
  ASSET = 'asset',
  USER = 'user'
}

type EntityIdentifierType = DeviceIdentifierType | AssetIdentifierType | RoleIdentifierType | FileMetaIdentifierType;
type Entities = Devices | Assets | Roles | InventoryDevices | DeviceFiles;
type ApiResult = DeviceAPIResult | AssetAPIResult | RoleAPIResult;
type ResultIdentifier = number | string;
type ResultIdentifiers = ResultIdentifier[];

/**
 * Parses the given bulk-operation subtasks for any nested entities to display as rows to user.
 *
 * @param subtasks bulk operation subtasks
 * @returns array of entities to be displayed as rows
 */
const getBulkOperationEntities = (subtasks: Subtask[], bulkOperation: BulkGroupAssignmentInfo) => {
  let result    = [];
  const {error} = bulkOperation || {};
  if (error) {
    const {code, description} = error || {};
    result.push({
      status    : AsyncTaskStatus.FAILED,
      reason    : description,
      errorCode : code
    });
  }
  subtasks?.forEach(subtask => {
    const {
      deviceFeatures,
      other,
      devices,
      provisionedDevices,
      assets,
      filemetas,
      roles,
      info,
      lastModifiedAt,
      name: taskName,
      status
    } = subtask || {};
    if (deviceFeatures?.length) {
      deviceFeatures.forEach(job => {
        const {name, lastModifiedOn, esn} = job;
        const features                    = other?.features;
        result.push({
          features       : features ? getFeaturesList(features) : translate('common:notAvailable'),
          status         : getFeaturesStatus(subtask, job),
          lastModifiedOn : formatDateTime(lastModifiedOn),
          name,
          esn
        });
      });
    } else {
      const entityInfoResponse = safeJSONParse(info.response?.body);
      if (entityInfoResponse && !isEmpty(entityInfoResponse)) {
        const {response: {errors, results, entityIdentifierType}} = entityInfoResponse;
        const isJob = isObject(results) && results.jobId;
        if (!isJob) {
          const {entityType, version} = other || {};
          const actionType = other?.bulkOperation?.actionType;
          const entities   = handleEntities({
            entityType,
            devices,
            assets,
            roles,
            provisionedDevices,
            filemetas
          });
          const idMaps = createDeviceMappings(entities, entityType);
          if (version) {
            result = getBulkOperationEntitiesV2(
              entities,
              entityInfoResponse,
              actionType,
              entityType,
              other
            );
          } else {
            if (entities?.length) {
              if (isGroupAssignmentSubtask(actionType)) {
                const {successes, failures}    = getGroupAssignmentResults(results);
                const failuresWithErrorDetails = [];
                const {updateErrors, readErrors, generalErrors} = parseGroupAssignmentErrors(errors);
                failuresWithErrorDetails.push(...generalErrors);
                Object.entries(readErrors).forEach(([identifier, error]) => {
                  failuresWithErrorDetails.push({
                    code    : error?.code,
                    message : error?.message,
                    identifier
                  });
                });
                !isObjectEmpty(failures) && Object.entries(failures).forEach(([groupId, failingIdentifiers]) => {
                  if (failingIdentifiers?.length) {
                    const groupError = updateErrors[groupId];
                    const populatedErrors = failingIdentifiers?.map(({identifier, action}) => ({
                      message : groupError?.message || translate(`admin-groups:edit.bulk.${action}`),
                      code    : groupError?.code,
                      identifier
                    }));
                    failuresWithErrorDetails.push(...populatedErrors);
                  }
                });
                failuresWithErrorDetails?.length && failuresWithErrorDetails.forEach(({identifier, message, code}) => {
                  const {name, lastModifiedOn}: ApiResult = resolveResultDetails(idMaps, identifier, entityType);
                  result.push({
                    lastModifiedOn : lastModifiedOn ? formatDateTime(lastModifiedOn) : '',
                    status         : AsyncTaskStatus.FAILED,
                    name           : name || identifier,
                    reason         : message,
                    errorCode      : code
                  });
                });

                // build rows for group assignment successes
                const sortedSuccesses = [];
                Object.entries(successes).forEach(([groupId, success]) => {
                  success.forEach(({identifier, action}) => {
                    sortedSuccesses.push({
                      identifier,
                      action,
                      groupId
                    });
                  });
                });
                sortedSuccesses.forEach(({identifier, action}) => {
                  const {name, lastModifiedOn}: ApiResult = resolveResultDetails(idMaps, identifier, entityType);
                  result.push({
                    lastModifiedOn : lastModifiedOn ? formatDateTime(lastModifiedOn) : '',
                    reason         : translate(`admin-groups:edit.bulk.${action}`),
                    name           : name || identifier,
                    status         : AsyncTaskStatus.SUCCESS
                  });
                });
              } else {
                if (errors.length) {
                  const parsedErrors: EntityErrors = [];
                  errors.forEach(error => {
                    const {updateErrors, readErrors, createErrors, error: generalError} = error || {};
                    generalError && parsedErrors.push({
                      identifier : '',
                      code       : generalError.code,
                      message    : generalError.description
                    });
                    readErrors && parsedErrors.push(...parseReadErrors(readErrors));
                    updateErrors && parsedErrors.push(...parseUpdateErrors(updateErrors, entityType));
                    createErrors && parsedErrors.push(...parseUpdateErrors(createErrors, entityType));
                    if (isEmpty(parsedErrors)) {
                      const {identifier, description, code} = error || {};
                      const identifierType          = getIdentifierType(entityType, entityIdentifierType);
                      const infoIdMap               = {[identifierType]: identifier};
                      const deviceIdentifierType    = getDeviceIdentifierType(entityType, infoIdMap) || identifierType;
                      result.push({
                        name           : identifier,
                        status         : AsyncTaskStatus.FAILED,
                        reason         : description,
                        errorCode      : code,
                        identifierType : identifier && deviceIdentifierType,
                        identifier
                      });
                    }
                  });
                  parsedErrors.forEach(error => {
                    const {identifier, message, code}       = error;
                    const {name, lastModifiedOn}: ApiResult = resolveResultDetails(idMaps, identifier, entityType);
                    result.push({
                      lastModifiedOn : lastModifiedOn ? formatDateTime(lastModifiedOn) : '',
                      name           : name || identifier,
                      status         : AsyncTaskStatus.FAILED,
                      reason         : message,
                      errorCode      : code
                    });
                  });
                }
                results.forEach(id => {
                  const identifierType       = getIdentifierType(entityType, entityIdentifierType);
                  const infoIdMap            = {[identifierType]: id};
                  const resolveResultData    = resolveResultDetails(idMaps, id, entityType) || {};
                  const deviceData           = !isEmpty(resolveResultData) ? resolveResultData : infoIdMap;
                  const selectedIdentifier   = getDeviceIdentifier(resolveResultData) || id;
                  const deviceIdentifierType = getDeviceIdentifierType(entityType, deviceData);
                  const {name, lastModifiedOn, features} = resolveResultData;
                  result.push({
                    features       : features ? getFeaturesList(features) : '',
                    lastModifiedOn : lastModifiedOn ? formatDateTime(lastModifiedOn) : '',
                    status         : AsyncTaskStatus.SUCCESS,
                    name           : name || id,
                    identifier     : selectedIdentifier,
                    identifierType : deviceIdentifierType
                  });
                });
              }
            } else {
              errors?.forEach(error => {
                const {identifier, code, description} = error?.description?.error || error?.error || error || {};
                const identifierType       = getIdentifierType(entityType, entityIdentifierType);
                const infoIdMap            = {[identifierType]: identifier};
                const deviceIdentifierType = getDeviceIdentifierType(entityType, infoIdMap) || identifierType;
                result.push({
                  status         : AsyncTaskStatus.FAILED,
                  reason         : description,
                  name           : identifier,
                  errorCode      : code,
                  identifierType : identifier && deviceIdentifierType,
                  identifier
                });
              });
              const {successes, failures} = getGroupAssignmentResults(results) || {};
              if (other?.bulkOperation?.actionType === BulkActionType.GROUP_ASSIGNMENT) {
                const sortedSuccesses = [];
                Object.entries(successes).forEach(([groupId, success]) => {
                  success.forEach(({identifier, action}) => {
                    sortedSuccesses.push({
                      identifier,
                      action,
                      groupId
                    });
                  });
                });
                sortedSuccesses.forEach(({identifier, action}) => {
                  result.push({
                    reason : translate(`admin-groups:edit.bulk.${action}`),
                    name   : identifier,
                    status : AsyncTaskStatus.SUCCESS
                  });
                });
              } else {
                results.forEach(id => {
                  const deviceTypeException = ((id && isObject(id)) && id?.searchDetails) || {};
                  if (deviceTypeException && !isEmpty(deviceTypeException)) {
                    const {identifierType, searchQuery} = deviceTypeException;
                    const {searchTerms: {serialNumber}} = searchQuery?.search;
                    const infoIdMap                     = {[identifierType]: serialNumber};
                    const deviceIdentifierType          = getDeviceIdentifierType(id?.entityType, infoIdMap);
                    result.push({
                      status         : AsyncTaskStatus.SUCCESS,
                      name           : serialNumber,
                      identifier     : serialNumber,
                      identifierType : serialNumber && deviceIdentifierType
                    });
                  } else {
                    const identifierType       = getIdentifierType(entityType, entityIdentifierType);
                    const infoIdMap            = {[identifierType]: id};
                    const deviceIdentifierType = getDeviceIdentifierType(entityType, infoIdMap);
                    result.push({
                      status         : AsyncTaskStatus.SUCCESS,
                      name           : id,
                      identifier     : id,
                      identifierType : deviceIdentifierType
                    });
                  }
                });
              }
            }
          }
        } else {
          result.push({
            name : results.jobId
          });
        }
      } else {
        result.push({
          lastModifiedOn : formatDateTime(lastModifiedAt),
          name           : taskName,
          status
        });
      };
    }
  });
  return result;
};

/**
 * Provides a simple convenience function which formats the `lastModifiedOn` value
 * in the context of bulk operations.
 */
const formatLastModifiedOn = (value: string): string => (
  value ? formatDateTime(value) : ''
);

/**
 * Utility which returns the Identifier Type name for the given device entityType.
 */
const getDeviceIdentifierType = (entityType, device) => {
  const {
    esn,
    serialNumber,
    iccid,
    meid,
    imei,
    imei2,
    imsi,
    mdn,
    externalId,
    ipAddress,
    deviceId,
    name,
    deviceTypeName
  } = device;

  let identifierType: identifierTypeColumnKeys;
  if (isOnlyDeviceEntity(entityType)) {
    if (esn) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.ESN];
    } else if (serialNumber) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.SERIAL_NUMBER];
    } else if (iccid) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.ICCID];
    } else if (meid) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.MEID];
    } else if (imei) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.IMEI];
    } else if (imei2) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.IMEI2];
    } else if (imsi) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.IMSI];
    } else if (mdn) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.MDN];
    } else if (externalId) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.EXTERNAL_ID];
    } else if (ipAddress) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.IP_ADDRESS];
    } else if ((deviceId)) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.DEVICE_ID];
    } else if ((name)) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.NAME];
    } else if ((deviceTypeName)) {
      identifierType = identifierTypeColumnKeys[DeviceIdentifierType.DEVICE_TYPE_NAME];
    }
  }
  return identifierType;
};

/**
 * Provides a convenience function for building a grid row from the given subtask entity.
 */
const processRow = ({
  identifier,
  code,
  description,
  action,
  error
}, {
  result,
  idMaps,
  entityType,
  entityIdentifierType,
  hasReason = false
},
type: 'success' | 'error' = 'success') => {
  const identifierType                   = getIdentifierType(entityType, entityIdentifierType);
  const idMapsData                       = idMaps?.[identifierType]?.[identifier] || {};
  const {name, lastModifiedOn, features} = idMapsData;
  const infoIdMap                        = {[identifierType]: identifier};
  const deviceData                       = !isEmpty(idMapsData) ? idMapsData : infoIdMap;
  const selectedIdentifier               = getDeviceIdentifier(idMapsData) || identifier;
  const deviceIdentifierType             = getDeviceIdentifierType(entityType, deviceData);
  const common = {
    lastModifiedOn : formatLastModifiedOn(lastModifiedOn),
    name           : name || identifier || entityType,
    features       : getFeaturesList(features),
    identifier     : selectedIdentifier,
    identifierType : deviceIdentifierType
  };
  if (type === 'error') {
    const reason    = description?.description || description || error?.description;
    const errorCode = code || error?.code;
    result.push({
      status : AsyncTaskStatus.FAILED,
      reason,
      errorCode,
      ...common
    });
  } else {
    result.push({
      status : AsyncTaskStatus.SUCCESS,
      reason : (hasReason && action) ? translate(`admin-groups:edit.bulk.${action}`) : '',
      ...common
    });
  }
};

/**
 * Convenience function for processing a successful bulk operation result.
 */
const processBulkOperationSuccess = (item, context) => processRow(item, context);

/**
 * Convenience function for processing a failed bulk bulk operation result.
 */
const processBulkOperationError   = (item, context) => processRow(item, context, 'error');

/**
 * Convenience function for processing a collection of bulk operation entities.
 */
const processBulkOperationSubtasks = ({result, results, errors, idMaps, entityType, entityIdentifierType}) => {
  errors?.forEach(function(item) {
    processBulkOperationError(item, {result, idMaps, entityType, entityIdentifierType});
  });
  results.forEach(identifier => {
    processBulkOperationSuccess({identifier}, {result, idMaps, entityType, entityIdentifierType});
  });
};

/**
 * Processes a replace bulk group assignment's operation's task results and adds any rows
 * to be displayed onto the result.
 *
 * Since we display 'row groups' when viewing a replace group assignment, we parse the
 * subtask differently.
 *
 */
const processReplaceGroupAssignmentSubtask = ({
  result,
  results,
  errors,
  idMaps,
  entityType,
  entityIdentifierType
}) => {
  const individualRows = new Map();
  let isNotArray = true;
  errors.forEach((groupIdErrors) => {
    Object.entries(groupIdErrors).forEach(([_groupId, errorDetails]) => {
      if (Array.isArray(errorDetails)) {
        isNotArray = false;
        errorDetails?.forEach(error => {
          const {identifier, code, description, action} = error || {};
          const existingValue = individualRows.get(identifier);
          const info = {
            reason    : description,
            status    : AsyncTaskStatus.FAILED,
            errorCode : code
          };
          individualRows.set(identifier, existingValue ? [...existingValue, info] : [info]);
        });
      }
    });
    if (isNotArray) {
      const {identifier, code, description, action} = groupIdErrors || {};
      const existingValue = individualRows.get(identifier);
      const info = {
        reason    : description,
        status    : AsyncTaskStatus.FAILED,
        errorCode : code
      };
      individualRows.set(identifier, existingValue ? [...existingValue, info] : [info]);
    }
  });
  Object.entries(results).forEach(([_, success]) => {
    Object.entries(success).forEach(([_, item]) => {
      item.forEach(({identifier, action}) => {
        const existingValue = individualRows.get(identifier);
        const info = {
          reason : translate(`admin-groups:edit.bulk.${action}`),
          status : AsyncTaskStatus.SUCCESS
        };
        individualRows.set(identifier, existingValue ? [...existingValue, info] : [info]);
      });
    });
  });
  individualRows.forEach((actions, key) => {
    const overallStatus  = getOverallGroupStatus(actions.map(({status}) => status));
    const identifierType = getIdentifierType(entityType, entityIdentifierType);
    const entityDetail   = idMaps[identifierType][key] || {};
    const lastModifiedOn = formatLastModifiedOn(entityDetail.lastModifiedOn);
    const name           = entityDetail.name || key;
    result.push({
      subResults : actions.map(action => ({...action, lastModifiedOn, name})),
      status     : overallStatus,
      reason     : '',
      errorCode  : '',
      lastModifiedOn,
      name
    });
  });
};

/**
 * Processes an add or remove bulk group assignment's operation's task results and adds any rows
 * to be displayed onto the result.
 *
 * The structure of the group assignment results are different so we need to iterate differently
 * to correctly display the results.
 *
 */
const processGroupAssignmentSubtask = ({
  result,
  results,
  errors,
  idMaps,
  entityType,
  entityIdentifierType
}) => {
  const isGroupAssignment = true;
  errors.forEach((groupIdErrors) => {
    if (groupIdErrors?.identifier) {
      processBulkOperationError(groupIdErrors, {
        result,
        idMaps,
        entityType,
        entityIdentifierType
      });
    } else {
      Object.entries(groupIdErrors).forEach(([_groupId, errorDetails]) => {
        errorDetails?.forEach(error => {
          processBulkOperationError(error, {
            result,
            idMaps,
            entityType,
            entityIdentifierType,
            hasReason : isGroupAssignment
          });
        });
      });
    }
  });
  Object.entries(results).forEach(([_, successes]) => {
    Object.entries(successes).forEach(([_groupId, success]) => {
      success.forEach(({identifier, action}) => {
        processBulkOperationSuccess({identifier, action}, {
          result,
          idMaps,
          entityType,
          entityIdentifierType,
          hasReason : isGroupAssignment
        });
      });
    });
  });
};

/**
 * A handler for parsing bulk operation entities from the V2 task details to display to the user as grid rows.
 *
 * @param entityDetails a list of entity details
 * @param entityInfoResponse the task info response holding failure / success information
 * @param actionType the type of bulk operation that the task represents for (e.g 'assignGroup', 'move', 'unpair')
 * @param entityType the entity type the bulk operation was performed on (e.g device, asset)
 * @param other the 'other' bulk operation details for determining how to parse entities
 * @returns a list of grid rows to be displayed
*/
// eslint-disable-next-line max-params
const getBulkOperationEntitiesV2 = (entityDetails, entityInfoResponse, actionType, entityType, other) => {
  const {response: {errors, results, entityIdentifierType}} = entityInfoResponse;
  const result = [];
  const idMaps = createDeviceMappings(entityDetails, entityType);
  const context = {result, results, errors, idMaps, entityType, entityDetails, entityIdentifierType};
  if (isGroupAssignmentSubtask(actionType)) {
    if (isReplaceGroupSubtask(other)) {
      processReplaceGroupAssignmentSubtask(context);
    } else {
      processGroupAssignmentSubtask(context);
    }
  } else {
    processBulkOperationSubtasks(context);
  }
  return result;
};

/**
 * Given a list of subresult statuses determines if the parent (group) row should
 * show a status of SUCCESS, FAILURE, or PARTIAL SUCCESS.
 *
 * [SUCCESS, FAILURE] = PARTIAL SUCCESS
 * [SUCCESS, SUCCESS] = SUCCESS
 * [FAILURE, FAILURE] = FAILURE
 *
 * @param statuses subresults for a group assignment expansion.
 * @returns a single overall status
 */
const getOverallGroupStatus = (statuses = []): AsyncTaskStatus => {
  let overallStatus;
  statuses.forEach((status, index) => {
    if (index === 0) {
      overallStatus = status;
    } else {
      if (status !== overallStatus) {
        overallStatus = AsyncTaskStatus.PARTIAL_SUCCESS;
      }
    }
  });
  return overallStatus;
};

/**
 * Given an entity type (device, asset, etc) and service entity identifier type, this helper function will determine the
 * correct identifier type to use to fetch entity details.
 *
 * @param entityType the entity type fetching identifier type for (device / asset / future entities)
 * @param entityIdentifierType the identifier type returned from the service
 * @returns adjusted identifier type
 */
const getIdentifierType = (entityType: Entities, entityIdentifierType: EntityIdentifierType): EntityIdentifierType => {
  if (isAssetsEntity(entityType)) {
    if (entityIdentifierType === AssetIdentifierType.ID) {
      return AssetIdentifierType.ASSET_ID;
    } else {
      return entityIdentifierType;
    }
  } else if (isDeviceEntity(entityType)) {
    if (entityIdentifierType === DeviceIdentifierType.ID) {
      return DeviceIdentifierType.DEVICE_ID;
    } else {
      return entityIdentifierType;
    }
  } else if (isRolesEntity(entityType)) {
    if (entityIdentifierType === RoleIdentifierType.ID) {
      return RoleIdentifierType.ROLE_ID;
    } else {
      return entityIdentifierType;
    }
  } else if (isFileMetasEntity(entityType)) {
    if (entityIdentifierType === FileMetaIdentifierType.FILE_META_ID) {
      return FileMetaIdentifierType.FILE_META_ID;
    } else {
      return entityIdentifierType;
    }
  }
  return entityIdentifierType;
};

export const isReplaceGroupSubtask = (other): boolean => !!(other?.bulkOperation?.actionDetails?.replaceGroup);

const isGroupAssignmentSubtask = (actionType): boolean => !!(actionType === BulkActionType.GROUP_ASSIGNMENT);
const isDeviceEntity           = (entityType): boolean => !!(isEqual(entityType, BulkActionEntity.DEVICES) ||
  isEqual(entityType, BulkActionEntity.PROVISIONEDDEVICE));
const isOnlyDeviceEntity       = (entityType): boolean => !!isEqual(entityType, BulkActionEntity.DEVICES);
const isAssetsEntity           = (entityType): boolean => !!isEqual(entityType, BulkActionEntity.ASSETS);
const isRolesEntity            = (entityType): boolean => !!isEqual(entityType, BulkActionEntity.ROLES);
const isFileMetasEntity        = (entityType): boolean => !!isEqual(entityType, BulkActionEntity.FILE_METAS);

/**
 * Utility for returning an array of the possible device identifier type values.
 */
const getDeviceIdentifierTypes = () => (
  Object.values(DeviceIdentifierType)
);

/**
 * Utility for returning an array of the possible asset identifier type values.
 */
const getAssetIdentifierTypes = () => (
  Object.values(AssetIdentifierType)
);

/**
 * Utility for returning an array of the possible role identifier type values.
 */
const getRoleIdentifierTypes = () => (
  Object.values(RoleIdentifierType)
);

/**
 * Utility for returning an array of the possible file meta identifier type values.
 */
const getFileMetaIdentifierTypes = () => (
  Object.values(FileMetaIdentifierType)
);

/**
 * Utility which returns an entities of specific task.
 */
const handleEntities = ({
  entityType,
  devices,
  assets,
  roles,
  provisionedDevices,
  filemetas
}) => {
  if (isEqual(entityType, BulkActionEntity.DEVICES)) {
    return devices;
  } else if (isEqual(entityType, BulkActionEntity.ASSETS)) {
    return assets;
  } else if (isEqual(entityType, BulkActionEntity.ROLES)) {
    return roles;
  } else if (isEqual(entityType, BulkActionEntity.PROVISIONEDDEVICE)) {
    return provisionedDevices;
  } else if (isEqual(entityType, BulkActionEntity.FILE_META)) {
    return filemetas;
  }
  return [];
};

/**
 * Utility which returns an mapping of specific task.
 */
const handleTasksMapping = (entityType) => {
  if (isEqual(entityType, BulkActionEntity.DEVICES) ||
    isEqual(entityType, BulkActionEntity.PROVISIONEDDEVICE)) {
    return getDeviceIdentifierTypes();
  } else if (isEqual(entityType, BulkActionEntity.ASSETS)) {
    return getAssetIdentifierTypes();
  } else if (isEqual(entityType, BulkActionEntity.ROLES)) {
    return getRoleIdentifierTypes();
  } else if (isEqual(entityType, BulkActionEntity.FILE_META)) {
    return getFileMetaIdentifierTypes();
  }
  return [];
};

/**
 * Utility which returns an object mapping entity identifier types to their respective id maps
 * to aid in the searchability of a entity by any of it's identifier types.
 *
 * The resulting map is formatted as such:
 * {
 *   esn: {
 *      [device.esn]: device,
 *   },
 *   deviceId: {
 *      [device.deviceId]: device,
 *   },
 *   ...,
 * }
 */
const createDeviceMappings = (entities: Entities, entityType) => {
  const map = {};
  const identifierTypes = handleTasksMapping(entityType);
  identifierTypes.forEach((type: EntityIdentifierType) => {
    map[type] = entities?.length ? idMap(entities, type) : {};
  });
  return map;
};

/**
 * Utility which returns the entity details for the given identifier via the provided map.
 */
const resolveResultDetails = (map, identifier, entityType): ApiResult => {
  const identifierTypes = handleTasksMapping(entityType);
  const foundType       = identifierTypes.find(type => map[type][identifier]);
  return foundType ? map[foundType][identifier] : {};
};

interface EntityError {
  identifier: number | string;
  message: string;
  code: string;
  description?: string;
  name?: string | number;
}

type EntityErrors = EntityError[];

interface ReadErrors {
  name: string[];
  readErrorsdescription: string;
  code: string;
  imei: string[];
  imei2: string[];
}

/**
 * Parses the read errors from the bulk operation errors result.
 * @param readErrors the entity read errors
 * @returns array of entity names for the failed reads
 */
const parseReadErrors = (readErrors: ReadErrors): EntityErrors => {
  const {readErrorsdescription, code} = readErrors || {};
  const identifiers =
    readErrors?.[DeviceIdentifierType.DEVICE_ID] ||
    readErrors?.[DeviceIdentifierType.ESN] ||
    readErrors?.[DeviceIdentifierType.SERIAL_NUMBER] ||
    readErrors?.[DeviceIdentifierType.MEID] ||
    readErrors?.[DeviceIdentifierType.ICCID] ||
    readErrors?.[DeviceIdentifierType.IMEI] ||
    readErrors?.[DeviceIdentifierType.IMEI2] ||
    readErrors?.[DeviceIdentifierType.IMSI] ||
    readErrors?.[DeviceIdentifierType.MDN] ||
    readErrors?.[AssetIdentifierType.ASSET_ID] ||
    readErrors?.[AssetIdentifierType.VIN] ||
    readErrors?.[AssetIdentifierType.LICENSE_NUMBER] ||
    readErrors?.[AssetIdentifierType.NAME] ||
    readErrors?.[RoleIdentifierType.ROLE_ID];
  return identifiers?.map(identifier => ({
    message : readErrorsdescription,
    identifier,
    code
  })) || [];
};

interface ErrorDescription {
  error: {
    code: number;
    description: string;
  }
}

interface UpdateDeviceErrors {
  code: string;
  updateErrorsdescription: string;
  deviceIds: [{
    error: {
      id,
      deviceId,
      description: string | ErrorDescription
    }
  }]
}

interface UpdateAssetErrors {
  code: string;
  updateErrorsdescription: string;
  assetIds: [{
    error: {
      id,
      description: string | ErrorDescription
    }
  }]
}

interface UpdateRoleErrors {
  code: string;
  updateErrorsdescription: string;
  roleIds: [{
    error: {
      id,
      description: string | ErrorDescription
    }
  }]
}

/**
 * Parses the update errors from the bulk operation errors result.
 *
 * @param updateErrors any entity update errors
 * @returns array of ids or descriptions for the failed updates
 */
const parseUpdateErrors = (
  updateErrors: UpdateDeviceErrors | UpdateAssetErrors | UpdateRoleErrors,
  entityType: BulkActionEntity
): EntityErrors => {
  if (isEqual(entityType, BulkActionEntity.DEVICES) || isEqual(entityType, BulkActionEntity.PROVISIONEDDEVICE)) {
    const {deviceIds} = updateErrors || {};
    return deviceIds?.[0]?.map(({error}) => {
      const {
        id,
        deviceId,
        esn,
        serialNumber,
        meid,
        iccid,
        imei,
        imei2,
        imsi,
        mdn,
        description,
        code
      } = error;
      return {
        identifier : id || deviceId || esn || serialNumber || meid || iccid || imei || imei2 || imsi || mdn,
        message    : isObject(description) ? description?.error?.description : description,
        code
      };
    }) || [];
  } else if (isEqual(entityType, BulkActionEntity.ASSETS)) {
    const {assetIds} = updateErrors || {};
    return assetIds?.[0]?.map(({error}) => {
      const {
        id,
        name,
        vin,
        licenseNumber,
        description,
        code
      } = error;
      return {
        identifier : id || name || vin || licenseNumber,
        message    : isObject(description) ? description?.error?.description : description,
        code
      };
    }) || [];
  } else if (isEqual(entityType, BulkActionEntity.ROLES)) {
    const {roleIds} = updateErrors || {};
    return roleIds?.[0]?.map(({error}) => {
      const {
        id,
        description,
        code
      } = error;
      return {
        identifier : id,
        message    : isObject(description) ? description?.error?.description : description,
        code
      };
    }) || [];
  }
};

interface GroupAssignmentUpdateErrors {
  code: string;
  updateErrorsdescription: string;
  groupIds: [[{
    error: {
      groupId: number,
      code: string,
      description: string
    }
  }]]
}

type GroupAssignmentReadErrorResults = {
  [key in DeviceIdentifierType]: ResultIdentifiers;
}

interface GroupAssignmentReadErrors extends GroupAssignmentReadErrorResults {
  code: string;
  readErrorsdescription: string;
}

const parseGroupAssignmentUpdateErrors = (
  updateErrors: GroupAssignmentUpdateErrors
): GroupAssignmentUpdateErrorsMap => {
  const groupErrors = {};
  const {groupIds, code, updateErrorsdescription} = updateErrors || {};
  groupIds?.[0]?.forEach(({error: {groupId}}) => {
    groupErrors[groupId] = {
      identifier : groupId,
      message    : updateErrorsdescription,
      code
    };
  });
  return groupErrors;
};

interface GroupAssignmentReadErrorsMap {
  [identifier: ResultIdentifier]: EntityError;
}

const parseGroupAssignmentReadErrors = (readErrors: GroupAssignmentReadErrors): GroupAssignmentReadErrorsMap => {
  const groupErrors = {};
  const {code, readErrorsdescription} = readErrors || {};
  const identifiers =
    readErrors?.[DeviceIdentifierType.DEVICE_ID] ||
    readErrors?.[DeviceIdentifierType.ESN] ||
    readErrors?.[DeviceIdentifierType.SERIAL_NUMBER] ||
    readErrors?.[DeviceIdentifierType.MEID] ||
    readErrors?.[DeviceIdentifierType.ICCID] ||
    readErrors?.[DeviceIdentifierType.IMEI] ||
    readErrors?.[DeviceIdentifierType.IMEI2] ||
    readErrors?.[DeviceIdentifierType.IMSI] ||
    readErrors?.[DeviceIdentifierType.MDN] ||
    readErrors?.[AssetIdentifierType.ASSET_ID] ||
    readErrors?.[AssetIdentifierType.VIN] ||
    readErrors?.[AssetIdentifierType.LICENSE_NUMBER] ||
    readErrors?.[AssetIdentifierType.NAME];

  identifiers?.forEach(identifier => {
    if (!isObject(identifier)) {
      groupErrors[identifier] = {
        message : readErrorsdescription,
        code
      };
    }
  });
  return groupErrors;
};

interface GroupAssignmentUpdateErrorsMap {
  [groupId: number]: EntityError;
};

interface GroupAssignmentError {
  updateErrors?: GroupAssignmentUpdateErrorsMap;
  readErrors?: GroupAssignmentReadErrorsMap;
  generalErrors?: EntityErrors;
}

/**
 * Parses the Group Assignment errors response and prepares for display to user.
 *
 * @param errors the errors array for a group assignment response
 * @returns an object containing any update errors, read errors, or general errors to be displayed to the user
 */
const parseGroupAssignmentErrors = (errors): GroupAssignmentError => {
  const result = {
    readErrors    : {},
    updateErrors  : {},
    generalErrors : []
  };
  if (errors.length) {
    errors.forEach(error => {
      const {updateErrors, readErrors, error: generalError} = error || {};
      if (!isObjectEmpty(updateErrors)) {
        result.updateErrors = {...result.updateErrors, ...parseGroupAssignmentUpdateErrors(updateErrors)};
      }
      if (!isObjectEmpty(readErrors)) {
        result.readErrors = {...result.readErrors, ...parseGroupAssignmentReadErrors(readErrors)};
      }
      if (!isObjectEmpty(generalError)) {
        result.generalErrors.push({identifier: '', message: generalError.description, code: generalError.code});
      }
    });
  }
  return result;
};

interface GroupAssignmentResult {
  [groupId: string]: {
    success: ResultIdentifiers,
    error: ResultIdentifiers,
    action: string;
  }
}

type GroupAssignmentResults = [GroupAssignmentResult];

interface BifurcatedGroupAssignmentResults {
  successes: {
    [groupId: number]: [{
      identifier: ResultIdentifier;
      action: string;
    }];
  };
  failures: {
    [groupId: number]: [{
      identifier: ResultIdentifier;
      action: string;
    }];
  };
}

/**
 * Parses the errors from the import operation errors result.
 *
 * @param ImportErrors any entity import errors
 * @returns array of name or descriptions, code for the failed entity
 */
const parseImportErrors = (errors) => {
  if (!isUndefined(errors?.[0]?.error)) {
    return errors?.map(({error}) => {
      const {code, description, devicename, name} = error || {};
      return {
        code,
        description : description?.name || description,
        name        : devicename || name
      };
    });
  } else {
    return errors?.map((item) => {
      return {
        name : item
      };
    });
  }
};

/**
 * Parses the expanded info of a bulk-operation response and returns the successes & failures for group assignments.
 *
 * @param results the results of the bulk operation
 * @returns arrays of ids for both successes and failures (names or ids)
 */
const getGroupAssignmentResults = (results: GroupAssignmentResults): BifurcatedGroupAssignmentResults => {
  const successes = {};
  const failures = {};
  results.forEach(result => {
    if (result && isObject(result)) {
      Object.entries(result).forEach(([groupId, groupResults]) => {
        const {error, success, action} = groupResults || {};
        successes[groupId] = success?.map(id => ({identifier: id, action}));
        failures[groupId] = error?.map(id => ({identifier: id, action}));
      });
    }
  });
  return {
    successes,
    failures
  };
};

/**
 * Determines which columns to exclude from the task status grid.
 *
 * @param requestType the request type if available.
 * @param actionType the action type if the request is bulk operation.
 * @param taskStatus the status of task if it is Failed, Success or Partial Success.
 * @returns array of column fields in which to exclude from displaying to user
 */
export const getStatusGridExcludedColumns = (
  requestType: RequestTypeLabels, actionType: BulkActionType, taskStatus: string): string[] => {
  const excludedColumns = [];
  if (!(isDeviceRequest(requestType) || isEnableServicesRequest(actionType) || isOnlyDeviceEntity(requestType))) {
    excludedColumns.push('identifier', 'identifierType', 'features');
  } else if ((!isEnableServicesRequest(actionType)) ||
    (isOnlyDeviceEntity(requestType) && taskStatus === AsyncTaskStatus.FAILED)) {
    excludedColumns.push('features');
  }
  return excludedColumns;
};

/**
 * Renders this Tooltip component in the TaskStatusGrid or any other Grid component, showing the
 * Grid cell text in tooltip.
 */
export const TaskStatusGridTooltip = (props: any): JSX.Element => {
  const {value} = props || {};
  const classes = tooltipStyle();
  return (
    <Tooltip classes={tooltipStyle()} title={<span>{value}</span>}
      placement={'bottom'}
      arrow>
      <span className={classes.align}>
        {value}
      </span>
    </Tooltip>
  );
};

/**
 * Utility for returning a boolean value based on status or any items status is FAILED.
 */
export const hasFailedJobStatus = (items: any[], status: string): boolean => {
  const hasFailed = status === AsyncTaskStatus.FAILED;
  if (!hasFailed) {
    return items.some(item => item.status === AsyncTaskStatus.FAILED);
  }
  return hasFailed;
};

/**
 * Utility for returning total entity count of the task.
 */
export const getExportTotalCount = ({other}) => {
  const {totalEntitiesCount} = other || {};
  if (!isNil(totalEntitiesCount)) {
    return totalEntitiesCount;
  }
  return '';
};

/**
 * Utility for returning Entity Name.
 */
export const getEntityName = (entityName: string, name: string) => {
  return translate('common:bulkActivityStatus.taskName', {
    entity : capitalizeFirstLetter(entityName),
    name
  });
};

/**
 * Utility for returning Task Nick Name or descriptive
 * based on the available data.
 */
export const getTaskNickName = (jobs: JobStatus) => {
  const {
    other : {
      requestType = '',
      taskNickName = ''
    } = {},
    name
  } = jobs || {};

  const deviceName = BulkActivityStatusEntityName.DEVICE;
  const assetName  = BulkActivityStatusEntityName.ASSET;
  const userName   = BulkActivityStatusEntityName.USER;

  if (isEqual(requestType, deviceName)) {
    return getEntityName(deviceName, name);
  }
  if (isEqual(requestType, assetName)) {
    return getEntityName(assetName, name);
  }
  if (isEqual(requestType, userName)) {
    return getEntityName(userName, name);
  }
  return taskNickName || name;
};
